import { useRef, useState } from "react";
import type { LucideIcon } from "lucide-react";
import { ChevronDown, Sparkles } from "lucide-react";
import gsap from "gsap";
import { useGSAP } from "@gsap/react";

import { NavLink } from "@/widgets/NavLink";
import {
  SidebarGroup,
  SidebarGroupContent,
  SidebarGroupLabel,
  SidebarMenuSub,
  SidebarMenuSubButton,
  SidebarMenuSubItem,
} from "@/shared/ui/sidebar";
import { useReducedMotion } from "@/shared/motion/useReducedMotion";
import { cn } from "@/shared/lib/utils";

export interface CollapsibleNavEntry {
  label: string;
  url: string;
  icon: LucideIcon;
  comingSoon?: boolean;
  /** 1002:入口旁小标(如客服舱「已停用 · 仅人工处理」)。 */
  badge?: string;
}

interface Props {
  label: string;
  /** localStorage 记忆键(跨会话记住每组展开态);同时派生 aria-controls id。 */
  storageKey: string;
  items: CollapsibleNavEntry[];
  isActive: (url: string) => boolean;
  comingSoonBadge: string;
  /** 旗舰款(「AI 能力」组):Sparkles 图标 + 主色渐变标题;其余组中性标题同款动效。 */
  accent?: boolean;
}

function readOpen(storageKey: string): boolean {
  try {
    return localStorage.getItem(storageKey) !== "0";
  } catch {
    return true;
  }
}

/**
 * 侧栏可折叠大类(2026-07-11):全部分组统一此渲染 —— 可折叠标题行 + 缩进子项列表。
 *
 * 动效纪律(ui-ux-pro-max):150~300ms、进 power2.out 出 power2.in、无循环动画、
 * reduced-motion 一律 gsap.set 直达终态;只动 transform/opacity/height(容器裁切),不抖布局。
 * 活跃竖轨骑在 SidebarMenuSub 自带的 border-l 轨道上,路由切换时在子项间滑动。
 */
export function CollapsibleNavGroup({
  label,
  storageKey,
  items,
  isActive,
  comingSoonBadge,
  accent = false,
}: Props) {
  const [open, setOpen] = useState(() => readOpen(storageKey));
  const reduced = useReducedMotion();
  const groupRef = useRef<HTMLDivElement>(null);
  const listRef = useRef<HTMLDivElement>(null);
  const railRef = useRef<HTMLSpanElement>(null);
  const iconRef = useRef<SVGSVGElement>(null);
  const mounted = useRef(false);

  const activeUrl = items.find((it) => isActive(it.url))?.url ?? null;
  const listId = `nav-group-${storageKey.replace(/[^a-zA-Z0-9-]/g, "-")}`;

  const toggle = () => {
    setOpen((prev) => {
      try {
        localStorage.setItem(storageKey, prev ? "0" : "1");
      } catch {
        /* 忽略写入失败(隐私模式等) */
      }
      return !prev;
    });
  };

  // 展开/收起:首帧与 reduced-motion 直达终态,交互时才动画。
  useGSAP(
    () => {
      const el = listRef.current;
      if (!el) return;
      const first = !mounted.current;
      mounted.current = true;
      gsap.set(el, { overflow: "hidden" });
      if (first || reduced) {
        gsap.set(el, { height: open ? "auto" : 0, autoAlpha: open ? 1 : 0 });
        return;
      }
      if (open) {
        gsap.to(el, { height: "auto", autoAlpha: 1, duration: 0.24, ease: "power2.out" });
        gsap.fromTo(
          el.querySelectorAll("[data-nav-group-item]"),
          { x: -8, autoAlpha: 0 },
          {
            x: 0,
            autoAlpha: 1,
            duration: 0.28,
            stagger: 0.03,
            delay: 0.05,
            ease: "power2.out",
            clearProps: "opacity,transform,visibility",
          }
        );
        // 旗舰款 Sparkles 一次性微呼吸(yoyo 一去一回,非循环动画)。
        if (accent && iconRef.current) {
          gsap.fromTo(
            iconRef.current,
            { scale: 1 },
            {
              scale: 1.18,
              duration: 0.15,
              yoyo: true,
              repeat: 1,
              ease: "power1.inOut",
              clearProps: "transform",
            }
          );
        }
      } else {
        gsap.to(el, { height: 0, autoAlpha: 0, duration: 0.18, ease: "power2.in" });
      }
    },
    { scope: groupRef, dependencies: [open, reduced] }
  );

  // 活跃竖轨:滑到当前路由所在子项(offsetTop 相对 listRef 的 relative 容器)。
  useGSAP(
    () => {
      const rail = railRef.current;
      const list = listRef.current;
      if (!rail || !list) return;
      const active = list.querySelector<HTMLElement>('[data-active="true"]');
      if (!active || !open) {
        gsap.set(rail, { autoAlpha: 0 });
        return;
      }
      const target = { y: active.offsetTop, height: active.offsetHeight, autoAlpha: 1 };
      if (reduced) {
        gsap.set(rail, target);
      } else {
        gsap.to(rail, { ...target, duration: 0.25, ease: "power3.out" });
      }
    },
    { scope: groupRef, dependencies: [activeUrl, open, reduced, items.length] }
  );

  return (
    <SidebarGroup ref={groupRef}>
      <SidebarGroupLabel asChild>
        <button
          type="button"
          onClick={toggle}
          aria-expanded={open}
          aria-controls={listId}
          className={cn(
            "flex w-full cursor-pointer items-center gap-1.5 text-2xs uppercase tracking-widest",
            !accent && "text-sidebar-muted"
          )}
        >
          {accent && <Sparkles ref={iconRef} className="h-3.5 w-3.5 shrink-0 text-primary" />}
          <span
            className={cn(
              accent &&
                "bg-gradient-to-r from-primary to-primary/60 bg-clip-text font-semibold text-transparent"
            )}
          >
            {label}
          </span>
          <ChevronDown
            className={cn(
              "ml-auto h-3.5 w-3.5 text-sidebar-muted transition-transform duration-200 ease-out",
              !open && "-rotate-90"
            )}
          />
        </button>
      </SidebarGroupLabel>
      <div id={listId} ref={listRef} className="relative">
        {/* 活跃竖轨:骑在 SidebarMenuSub border-l 轨道(mx-3.5 → 14px)上滑动。 */}
        <span
          ref={railRef}
          aria-hidden
          className="absolute left-[13px] top-0 w-[2px] rounded-full bg-primary opacity-0 shadow-[0_0_8px] shadow-primary/60"
        />
        <SidebarGroupContent>
          <SidebarMenuSub>
            {items.map((item) => (
              <SidebarMenuSubItem
                key={item.url}
                data-nav-group-item
                className={item.comingSoon ? "opacity-55" : undefined}
              >
                <SidebarMenuSubButton asChild isActive={isActive(item.url)}>
                  <NavLink
                    to={item.url}
                    className="transition-colors duration-200"
                    activeClassName="bg-sidebar-accent text-sidebar-accent-foreground"
                  >
                    <item.icon className="h-4 w-4" />
                    <span className="flex flex-1 items-center gap-1.5 text-[13px]">
                      {item.label}
                      {item.comingSoon && (
                        <span className="pointer-events-none ml-auto rounded bg-sidebar-border/60 px-1 py-px text-2xs leading-none text-sidebar-muted">
                          {comingSoonBadge}
                        </span>
                      )}
                      {item.badge && (
                        <span
                          data-testid="nav-item-badge"
                          className="pointer-events-none ml-auto rounded bg-warning/15 px-1 py-px text-2xs leading-none text-warning"
                        >
                          {item.badge}
                        </span>
                      )}
                    </span>
                  </NavLink>
                </SidebarMenuSubButton>
              </SidebarMenuSubItem>
            ))}
          </SidebarMenuSub>
        </SidebarGroupContent>
      </div>
    </SidebarGroup>
  );
}
